import { Injectable } from '@angular/core';
import { SUPPORT_EMAIL, SUPPORT_PHONE } from '../support-contact';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { UserAuthService } from '../UserAuth.service';

/**
 * Chunked, resumable project upload.
 *
 * Replaces the Reader's single multipart POST, which FAILS on large projects:
 * one request carrying the whole zip has to survive end to end, so a momentary
 * drop 40 minutes in loses everything, and hosts cap request bodies well below
 * the size of a real cure.
 *
 * Shape of the flow:
 *
 *   1. GET  GetSASForUpload?projectName&fileName&code   -> a write SAS for the blob
 *   2. PUT  {sas}&comp=block&blockid=<id>               -> one call per chunk
 *   3. PUT  {sas}&comp=blocklist                        -> commit, atomically
 *   4. POST ImportProject { blob reference }            -> server-side import
 *
 * Step 3 is what makes this safe: blocks are staged and invisible until the block
 * list is committed, so an interrupted upload leaves no half-written blob for the
 * importer to trip over. Step 4's contract is not published yet — see
 * importProject().
 *
 * RESUMABILITY, precisely. Within a session, a failed block is retried with
 * backoff and only the blocks that never landed are re-sent, so a drop costs one
 * chunk rather than the whole file. Across a page RELOAD it cannot resume: the
 * browser's File handle does not survive, so there is nothing left to read from.
 * Saying so plainly matters more than implying a durability we cannot deliver —
 * the user simply re-picks the file, and staged blocks are discarded by Azure.
 */

/** 4 MiB: large enough that per-block overhead is noise, small enough to retry cheaply. */
export const BLOCK_SIZE_BYTES = 4 * 1024 * 1024;

/** Azure's hard limit on blocks in one blob. */
export const MAX_BLOCKS = 50_000;

/** Retries per block before the upload is declared failed. */
export const MAX_BLOCK_ATTEMPTS = 4;

/** Largest project we will attempt, so a mis-picked file fails fast and clearly. */
export const MAX_UPLOAD_BYTES = BLOCK_SIZE_BYTES * MAX_BLOCKS;

/** What went wrong, in terms the UI can act on. NEVER swallowed. */
export type UploadFailureKind =
  | 'no-file'
  | 'not-a-zip'
  | 'too-large'
  | 'sas-denied'
  | 'sas-failed'
  | 'network'
  /** The request never got a response: blocked before sending, or the link died. */
  | 'blocked'
  /** The device itself reports no network. */
  | 'offline'
  | 'blob-rejected'
  | 'commit-failed'
  | 'import-not-authorized'
  | 'import-failed'
  | 'import-not-available'
  | 'cancelled';

/**
 * An upload failure carrying a message meant for a HUMAN.
 *
 * `message` is what gets shown. It is never a stringified exception or a status
 * code on its own: the person looking at it needs to know whether to retry, pick
 * a different file, or call someone.
 */
/**
 * A status of 0 is not a server answer — it is the ABSENCE of one.
 *
 * WHY THIS EXISTS. A cross-origin PUT that fails CORS is blocked by the browser
 * before any response reaches JavaScript: Angular sees status 0, never the 403 and
 * never the "CORS not enabled or no matching rule found" text, both of which exist
 * only in the preflight and only in the console. The old code therefore fell
 * through to the transient-network branch, retried four times, and told the user
 * to "check the connection" — a friendly, plausible, WRONG message that sends
 * someone to look at their wifi because a storage account is misconfigured.
 *
 * A genuinely dropped connection produces the same status 0, so this deliberately
 * does NOT assert CORS. `navigator.onLine === false` is the one case the browser
 * can state definitely; everything else is worded to be true whichever it is.
 *
 * NEVER RETRIED. A request blocked before it left the browser cannot succeed on a
 * second attempt, and at four attempts per block that is 3.5 s of delay per block
 * bought in exchange for the wrong conclusion.
 */
export function classifyNoResponse(online: boolean): {
  kind: UploadFailureKind;
  message: string;
} {
  if (!online) {
    return {
      kind: 'offline',
      message:
        'Your device reports that it is offline, so the upload stopped before ' +
        'anything was sent. Reconnect and try again — nothing was saved.',
    };
  }
  return {
    kind: 'blocked',
    message:
      'The upload could not reach storage, and the browser gave no response ' +
      'explaining why. This is usually a storage configuration problem rather ' +
      'than a fault on your side, though a dropped connection can look the same. ' +
      `Nothing was saved. If it keeps happening, contact Vericure Support at ${SUPPORT_EMAIL} or ${SUPPORT_PHONE}.`,
  };
}

/** Whether the device believes it has a network. Defensive: assume online. */
function deviceOnline(): boolean {
  try {
    return typeof navigator === 'undefined' || navigator.onLine !== false;
  } catch {
    return true;
  }
}

export class UploadError extends Error {
  constructor(
    readonly kind: UploadFailureKind,
    message: string,
    readonly detail?: string,
    readonly status?: number
  ) {
    super(message);
    this.name = 'UploadError';
  }
}

export interface UploadProgress {
  /** 0-100, across staging AND commit, so the bar does not stall at 100. */
  percent: number;
  bytesSent: number;
  totalBytes: number;
  blocksDone: number;
  blocksTotal: number;
  phase: 'preparing' | 'uploading' | 'committing' | 'importing' | 'done';
}

export interface UploadResult {
  projectName: string;
  fileName: string;
  blobUrl: string;
  /** Present once the import succeeds; null while the seam is disabled. */
  import: ImportResult | null;
}

/** ImportProject's 200 body. */
export interface ImportResult {
  status: string;
  projectName: string;
  serial: string;
  ticksImported: number;
  dataTypes: string[];
  projectDataWritten: boolean;
}

/**
 * The blob container is deliberately NOT declared here.
 *
 * GetSASForUpload chooses where the upload lands, and ImportProject defaults to
 * the same place. Naming it on the client would mean two systems holding the
 * same fact, and they drifted the moment the server's default changed: the SAS
 * wrote to one container while the import read another, so every import 404'd
 * on a blob that had uploaded perfectly. Omitting it keeps one owner.
 */

/** Validate before a single byte moves. */
export function validateFile(file: File | null | undefined): UploadError | null {
  if (!file) {
    return new UploadError('no-file', 'Choose a project file to upload.');
  }
  // Users export a standard .zip. `.vzip` is tolerated silently and never
  // mentioned in the UI: the DataServices contract names it, so accepting one
  // costs nothing, but telling people to look for an extension they do not have
  // sends them hunting for a file that does not exist.
  if (!/\.(zip|vzip)$/i.test(file.name)) {
    return new UploadError(
      'not-a-zip',
      'That file is not a Vericure project. Choose the .zip exported by Vericure Desktop.',
      file.name
    );
  }
  if (file.size <= 0) {
    return new UploadError('not-a-zip', 'That file is empty.', file.name);
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return new UploadError(
      'too-large',
      `That project is ${formatBytes(file.size)}, larger than the ${formatBytes(
        MAX_UPLOAD_BYTES
      )} maximum.`,
      file.name
    );
  }
  return null;
}

/** Project name from the zip's filename, which is what Desktop names it. */
export function projectNameFromFile(file: File): string {
  return file.name.replace(/\.(zip|vzip)$/i, '').trim();
}

/**
 * Block IDs must all be the SAME LENGTH once base64-encoded, or Azure rejects the
 * block list — a fixed-width decimal index is the simplest thing that satisfies it.
 */
export function blockIdFor(index: number): string {
  return btoa(`block-${String(index).padStart(6, '0')}`);
}

/** The commit payload: every staged block, in order. */
export function blockListXml(blockIds: readonly string[]): string {
  const latest = blockIds.map((id) => `<Latest>${id}</Latest>`).join('');
  return `<?xml version="1.0" encoding="utf-8"?><BlockList>${latest}</BlockList>`;
}

/** Append a query parameter to a SAS URI, which already carries its own query. */
export function withQuery(sasUri: string, extra: Record<string, string>): string {
  const joiner = sasUri.includes('?') ? '&' : '?';
  const query = Object.entries(extra)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  return `${sasUri}${joiner}${query}`;
}

export function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(0)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} bytes`;
}

/** How many blocks a file needs. */
export function blockCountFor(size: number, blockSize = BLOCK_SIZE_BYTES): number {
  return Math.max(1, Math.ceil(size / blockSize));
}

@Injectable({ providedIn: 'root' })
export class ProjectUploadService {
  private cancelled = false;

  constructor(
    private http: HttpClient,
    private auth: UserAuthService
  ) {}

  /** Ask the caller to stop after the block in flight. */
  cancel(): void {
    this.cancelled = true;
  }

  /**
   * Upload a project zip and hand the blob to the importer.
   *
   * Throws `UploadError` — never a bare rejection, and never resolves on failure.
   * Every exit is either a completed import or an error the UI can show.
   */
  async upload(
    file: File,
    onProgress: (p: UploadProgress) => void
  ): Promise<UploadResult> {
    this.cancelled = false;

    const invalid = validateFile(file);
    if (invalid) {
      throw invalid;
    }

    const projectName = projectNameFromFile(file);
    const fileName = file.name;
    const totalBytes = file.size;
    const blocksTotal = blockCountFor(totalBytes);

    onProgress({
      percent: 0,
      bytesSent: 0,
      totalBytes,
      blocksDone: 0,
      blocksTotal,
      phase: 'preparing',
    });

    const sasUri = await this.getSasUri(projectName, fileName);

    // Staging is 95% of the bar and commit the last 5%, so the bar keeps moving
    // through the commit instead of sitting at 100% while the request is in flight.
    const blockIds: string[] = [];
    let bytesSent = 0;

    for (let index = 0; index < blocksTotal; index++) {
      if (this.cancelled) {
        throw new UploadError('cancelled', 'Upload cancelled.');
      }
      const start = index * BLOCK_SIZE_BYTES;
      const chunk = file.slice(start, Math.min(start + BLOCK_SIZE_BYTES, totalBytes));
      const blockId = blockIdFor(index);

      await this.putBlockWithRetry(sasUri, blockId, chunk, index);

      blockIds.push(blockId);
      bytesSent += chunk.size;
      onProgress({
        percent: Math.round((bytesSent / totalBytes) * 95),
        bytesSent,
        totalBytes,
        blocksDone: index + 1,
        blocksTotal,
        phase: 'uploading',
      });
    }

    onProgress({
      percent: 95,
      bytesSent,
      totalBytes,
      blocksDone: blocksTotal,
      blocksTotal,
      phase: 'committing',
    });
    await this.commitBlockList(sasUri, blockIds);

    onProgress({
      percent: 97,
      bytesSent,
      totalBytes,
      blocksDone: blocksTotal,
      blocksTotal,
      phase: 'importing',
    });
    const blobUrl = sasUri.split('?')[0];
    const imported = await this.importProject(projectName, fileName, blobUrl);

    onProgress({
      percent: 100,
      bytesSent,
      totalBytes,
      blocksDone: blocksTotal,
      blocksTotal,
      phase: 'done',
    });
    return { projectName, fileName, blobUrl, import: imported };
  }

  /** GET a write SAS for `projectName/fileName`. */
  async getSasUri(projectName: string, fileName: string): Promise<string> {
    const params = new HttpParams()
      .set('projectName', projectName)
      .set('fileName', fileName);

    try {
      const response = await firstValueFrom(
        this.http.get(`${environment.apiUrl}GetSASForUpload`, {
          params,
          responseType: 'text',
        })
      );
      // The endpoint returns a JSON-encoded URI string, not an object.
      const uri = (response ?? '').trim().replace(/^"|"$/g, '');
      if (!uri.startsWith('http')) {
        throw new UploadError(
          'sas-failed',
          'The server did not return a valid upload location.',
          uri.slice(0, 200)
        );
      }
      return uri;
    } catch (err) {
      if (err instanceof UploadError) {
        throw err;
      }
      const http = err as HttpErrorResponse;
      if (http?.status === 401 || http?.status === 403) {
        throw new UploadError(
          'sas-denied',
          'You are not permitted to upload to this account.',
          this.detailOf(http),
          http.status
        );
      }
      throw new UploadError(
        'sas-failed',
        'Could not start the upload. The server did not provide an upload location.',
        this.detailOf(http),
        http?.status
      );
    }
  }

  /**
   * Stage one block, retrying transient failures.
   *
   * Retries only what MIGHT succeed on a second try — a dropped connection or a
   * 5xx. A 403 means the SAS is wrong or expired and will never succeed, so
   * retrying it just delays telling the user something true.
   */
  private async putBlockWithRetry(
    sasUri: string,
    blockId: string,
    chunk: Blob,
    index: number
  ): Promise<void> {
    let lastDetail = '';
    for (let attempt = 1; attempt <= MAX_BLOCK_ATTEMPTS; attempt++) {
      if (this.cancelled) {
        throw new UploadError('cancelled', 'Upload cancelled.');
      }
      try {
        await firstValueFrom(
          this.http.put(withQuery(sasUri, { comp: 'block', blockid: blockId }), chunk, {
            headers: new HttpHeaders({ 'x-ms-version': '2020-10-02' }),
            responseType: 'text',
          })
        );
        return;
      } catch (err) {
        const http = err as HttpErrorResponse;
        lastDetail = this.detailOf(http);

        // No response at all. Not transient, not a server verdict — see
        // classifyNoResponse. Thrown immediately: retrying cannot change it.
        if (http?.status === 0) {
          const online = deviceOnline();
          const { kind, message } = classifyNoResponse(online);
          throw new UploadError(kind, message, `status 0; navigator.onLine=${online}`, 0);
        }

        const permanent = http?.status === 403 || http?.status === 400;
        if (permanent) {
          throw new UploadError(
            'blob-rejected',
            'The upload was rejected by storage. The upload link may have expired — try again.',
            lastDetail,
            http.status
          );
        }
        if (attempt === MAX_BLOCK_ATTEMPTS) {
          throw new UploadError(
            'network',
            `Upload failed at part ${index + 1} after ${MAX_BLOCK_ATTEMPTS} attempts. Check the connection and try again.`,
            lastDetail,
            http?.status
          );
        }
        // Exponential backoff: 0.5s, 1s, 2s.
        await delay(500 * 2 ** (attempt - 1));
      }
    }
  }

  /** Commit the staged blocks. Until this lands, the blob does not exist. */
  private async commitBlockList(sasUri: string, blockIds: string[]): Promise<void> {
    try {
      await firstValueFrom(
        this.http.put(withQuery(sasUri, { comp: 'blocklist' }), blockListXml(blockIds), {
          headers: new HttpHeaders({
            'Content-Type': 'application/xml',
            'x-ms-version': '2020-10-02',
            'x-ms-blob-content-type': 'application/zip',
          }),
          responseType: 'text',
        })
      );
    } catch (err) {
      const http = err as HttpErrorResponse;
      if (http?.status === 0) {
        const online = deviceOnline();
        const { kind, message } = classifyNoResponse(online);
        throw new UploadError(kind, message, `status 0; navigator.onLine=${online}`, 0);
      }
      throw new UploadError(
        'commit-failed',
        'The upload finished but could not be finalised. Nothing was saved — please try again.',
        this.detailOf(http),
        http?.status
      );
    }
  }

  /**
   * ============================ CONTRACT SEAM ============================
   *
   * Hand the committed blob to the server for import.
   *
   * ImportProject DOES NOT EXIST YET: it 404s on beta-dev and appears in no
   * DataServices branch. Everything above this line runs against contracts that
   * are confirmed and tested; this one method is the only part waiting, and it is
   * deliberately isolated so wiring it is a single edit.
   *
   * What is still needed from DS: the route, the request shape (this currently
   * assumes projectName / fileName / blobUrl), the success response, and — most
   * importantly — the exact body of the 403 for a device serial the caller's
   * company may not access. That message must be shown to the user verbatim
   * rather than paraphrased, so it cannot be invented here.
   *
   * Until then this fails LOUDLY. Returning success would be worse than useless:
   * the blob would sit in storage, no project would appear, and the user would be
   * told everything worked.
   */
  async importProject(
    projectName: string,
    fileName: string,
    _blobUrl: string
  ): Promise<ImportResult | null> {
    const objectId = this.auth?.getObjectId?.() ?? '';
    const tenantId = this.auth?.getCurrentCustomerTenantId?.() ?? '';
    const customerId = this.auth?.getActiveCustomerId?.() ?? '';

    // The server requires objectId, and tenantId unless customerId is given.
    // Checking here turns a 400 round trip into an immediate, specific message.
    if (!objectId || (!tenantId && !customerId)) {
      throw new UploadError(
        'import-failed',
        'Could not identify your account for the import. Sign out and back in, then try again.',
        `objectId=${objectId ? 'yes' : 'no'} tenantId=${tenantId ? 'yes' : 'no'}`
      );
    }

    let params = new HttpParams()
      .set('objectId', objectId);
    if (tenantId) {
      params = params.set('tenantId', tenantId);
    }
    if (customerId) {
      params = params.set('customerId', customerId);
    }

    // blobName must be the EXACT "<projectName>/<fileName>" handed to
    // GetSASForUpload — the server locates the blob by that path, so any
    // divergence here surfaces as 404 blob_not_found after a full upload.
    //
    // blobContainer is OMITTED on purpose. The server honours it when supplied,
    // so sending a value would override the server's own default — which is
    // exactly how a corrected default got silently reverted by the client.
    // Leaving it out means the container can change server-side with no client
    // deploy at all.
    const body = {
      blobName: `${projectName}/${fileName}`,
    };

    try {
      return await firstValueFrom(
        this.http.post<ImportResult>(`${environment.apiUrl}ImportProject`, body, {
          params,
        })
      );
    } catch (err) {
      throw this.importErrorFor(err as HttpErrorResponse);
    }
  }

  /**
   * Map an ImportProject failure onto something the user can act on.
   *
   * The server sends a uniform { error, message } and its `message` is already
   * written for a human, so it is shown VERBATIM. Paraphrasing the 403 in
   * particular would drop the device serial, which is the one fact that tells an
   * operator whether they picked the wrong project or need access granted.
   */
  private importErrorFor(http: HttpErrorResponse): UploadError {
    const code = this.serverCodeOf(http);
    const message = this.serverMessageOf(http);

    if (http?.status === 403 && code === 'not_authorized_for_device_serial') {
      const serial = this.serverFieldOf(http, 'serial');
      return new UploadError(
        'import-not-authorized',
        message ??
          `Your company is not authorized for device serial '${serial ?? 'unknown'}'. Import aborted — nothing was written.`,
        serial ? `Device serial: ${serial}` : undefined,
        403
      );
    }

    // Everything else keeps the server's wording where there is one, and falls
    // back to text that still says what happened and what to do.
    const fallback: Record<string, string> = {
      bad_request: 'The import request was incomplete. Please try the upload again.',
      forbidden: 'Your account is not permitted to import projects.',
      blob_not_found:
        'The uploaded file could not be found on the server. Please upload it again.',
      invalid_project:
        'That file is not a readable Vericure project. Check you picked the .zip exported by Vericure Desktop.',
      import_failed:
        'The file uploaded and you are authorised for the device, but the server could not read the project.',
    };

    return new UploadError(
      'import-failed',
      message ?? fallback[code ?? ''] ??
        'The project was uploaded but the server could not import it.',
      this.detailOf(http),
      http?.status
    );
  }

  /** The stable machine code the server sends alongside the message. */
  private serverCodeOf(http: HttpErrorResponse | undefined): string | null {
    return this.serverFieldOf(http, 'error');
  }

  /** One field out of the server's JSON error body, whatever form it arrived in. */
  private serverFieldOf(
    http: HttpErrorResponse | undefined,
    field: string
  ): string | null {
    let body: unknown = http?.error;
    if (typeof body === 'string') {
      try {
        body = JSON.parse(body);
      } catch {
        return null;
      }
    }
    const value = (body as Record<string, unknown> | null)?.[field];
    return typeof value === 'string' && value.trim() ? value : null;
  }

  /** The server's own error text, when it sent one. */
  private serverMessageOf(http: HttpErrorResponse | undefined): string | null {
    const body = http?.error;
    if (typeof body === 'string' && body.trim()) {
      try {
        const parsed = JSON.parse(body);
        const msg = parsed?.error ?? parsed?.message ?? parsed?.Message;
        if (typeof msg === 'string' && msg.trim()) {
          return msg;
        }
      } catch {
        return body.slice(0, 400);
      }
    }
    if (body && typeof body === 'object') {
      const msg =
        (body as { error?: string; message?: string; Message?: string }).error ??
        (body as { message?: string }).message ??
        (body as { Message?: string }).Message;
      if (typeof msg === 'string' && msg.trim()) {
        return msg;
      }
    }
    return null;
  }

  /** Diagnostic detail, kept out of the headline message. */
  private detailOf(http: HttpErrorResponse | undefined): string {
    if (!http) {
      return '';
    }
    const server = this.serverMessageOf(http);
    return server ?? `${http.status ?? ''} ${http.statusText ?? ''}`.trim();
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
